import { Menu } from '@mantine/core'
import styled, { css } from 'styled-components'
import { Flex } from '../flex'
import { Select } from '../select'
import { Typography } from '../typography'
import { LanguagePickerProps } from './language-picker.types'
import { Icon } from '../icon'
import { theme } from '../../theme'

const StyledMenu = styled(Menu)`
  ${({ theme }) => css`
    position: relative;
    border-radius: ${theme.borderRadii.medium};
    z-index: ${theme.zIndex.languagePicker} !important;
  `}
`

const StyledDropdown = styled(Menu.Dropdown)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.sizes[3]};
    border-radius: ${theme.borderRadii.medium};
    background-color: ${theme.colors.white};
    box-shadow: 0px 8px 32px rgba(33, 37, 53, 0.16);
    border: none;
    z-index: ${theme.zIndex.languagePicker} !important;
    min-width: 320px;
    max-width: 320px;
  `}
`

const StyledButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    padding: ${theme.sizes.xs};
    border: 1px solid transparent;
    border-radius: ${theme.borderRadii.medium};
    cursor: pointer;
    font-size: ${theme.fontSizes.bodySmall};
    font-weight: ${theme.fontWeights.medium};
    color: ${theme.colors.primary400};
    gap: ${theme.sizes.xxs};
    align-items: center;
    background-color: #fff;

    :hover {
      background-color: ${theme.colors.primary30};
      border-color: ${theme.colors.primary100};
    }

    :active {
      background-color: ${theme.colors.primary100};
      border-color: ${theme.colors.primary100};
    }
  `}
`

function getShortLocaleCode(locale: string): string {
  return locale.split('-')[0].toUpperCase()
}

export function LanguagePicker({
  options,
  value,
  onChange,
  title,
  triggerLabel,
  position = 'bottom-end',
  selectOrientation,
  dataTestId = 'language-picker',
  children,
}: LanguagePickerProps) {
  const selectedOption = options.find((option) => option.value === value)

  return (
    <StyledMenu
      position={position}
      closeOnItemClick={false}
      withinPortal
      portalProps={{ style: { zIndex: theme.zIndex.languagePicker } }}
    >
      <Menu.Target>
        <StyledButton
          aria-haspopup="menu"
          aria-label={triggerLabel}
          data-testid={`${dataTestId}-trigger`}
        >
          <Icon name="globe" color="primary400" size="s" />
          {getShortLocaleCode(value)}
        </StyledButton>
      </Menu.Target>

      <StyledDropdown>
        <Flex direction="column" gap="xs">
          <Typography variant="default-bold">{title}</Typography>
          <Select
            options={options}
            defaultValue={selectedOption}
            onChange={(option) => onChange(option.value)}
            data-testid={`${dataTestId}-select`}
            size="small"
            orientation={selectOrientation}
          />

          {children}
        </Flex>
      </StyledDropdown>
    </StyledMenu>
  )
}
